<div class="mt-6 w-full flex flex-col gap-5">
  <p-toast></p-toast>

  <p-card>
    <ng-template #title>
      <div class="flex items-center gap-3">
        <p-button
          icon="fa fa-arrow-left"
          [text]="true"
          [rounded]="true"
          severity="secondary"
          (click)="navigateBack()"
        />
        <h1>
          @if (isEditMode()) {
            {{ 'defect.editTitle' | translate }}
          } @else {
            {{ 'defect.add' | translate }}
          }
        </h1>
      </div>
    </ng-template>

    <form
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
      class="flex flex-col gap-6"
    >
      <div class="flex flex-col md:flex-row gap-6">
        <div class="w-full md:w-2/3">
          <!-- Title -->
          <div class="flex flex-col gap-2">
            <label for="title">{{ 'defect.title' | translate }} *</label>
            <input
              pInputText
              id="title"
              formControlName="title"
              [placeholder]="'defect.title' | translate"
              class="w-full"
              [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('title') }"
            />
            @if (isInvalid('title')) {
              <small class="text-red-500">{{
                getErrorKey('title') | translate
              }}</small>
            }
          </div>

          <!-- Type Selection -->
          <div class="flex flex-col gap-2">
            <label>{{ 'defect.type' | translate }}</label>
            <p-selectbutton
              [options]="modelTypeOptions"
              optionLabel="value"
              optionValue="key"
              formControlName="modelType"
              (onChange)="onModelTypeChange()"
            />
          </div>

          <!-- Model Selection -->
          <div class="flex flex-col gap-2">
            @if (form.value.modelType === 'route_section') {
              <label for="modelId"
                >{{ 'defect.selectRouteSection' | translate }} *</label
              >
              <p-select
                inputId="modelId"
                [options]="routeSectionOptions()"
                optionLabel="label"
                optionValue="value"
                optionGroupLabel="label"
                optionGroupChildren="items"
                [group]="true"
                formControlName="modelId"
                [placeholder]="'defect.selectRouteSection' | translate"
                class="w-full"
                appendTo="body"
                scrollHeight="400px"
                [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('modelId') }"
              />
            } @else {
              <label for="modelId"
                >{{ 'defect.selectPsa' | translate }} *</label
              >
              <p-select
                inputId="modelId"
                [options]="psaOptions()"
                optionLabel="label"
                optionValue="value"
                formControlName="modelId"
                [placeholder]="'defect.selectPsa' | translate"
                class="w-full"
                appendTo="body"
                [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('modelId') }"
              />
            }
            @if (isInvalid('modelId')) {
              <small class="text-red-500">{{
                getErrorKey('modelId') | translate
              }}</small>
            }
          </div>
          <!-- Description -->
          <div class="flex flex-col gap-2">
            <label for="description"
              >{{ 'defect.description' | translate }} *</label
            >
            <textarea
              pTextarea
              id="description"
              formControlName="description"
              [placeholder]="'defect.description' | translate"
              rows="4"
              class="w-full"
              [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('description') }"
            ></textarea>
            @if (isInvalid('description')) {
              <small class="text-red-500">{{
                getErrorKey('description') | translate
              }}</small>
            }
          </div>
        </div>
        <div class="w-full md:w-1/3">
          <!-- Status -->
          <div class="flex flex-col gap-2">
            <label for="status">{{ 'defect.status' | translate }}</label>
            <p-select
              inputId="status"
              [options]="statusOptions"
              optionLabel="value"
              optionValue="key"
              formControlName="status"
              class="w-full"
            />
          </div>

          <!-- Responsible User -->
          <div class="flex flex-col gap-2">
            <label for="responsibleUser">{{
              'defect.responsibleUser' | translate
            }}</label>
            <p-select
              inputId="responsibleUser"
              [options]="responsibleUserOptions()"
              optionLabel="label"
              optionValue="value"
              formControlName="responsibleUserId"
              [placeholder]="'defect.responsibleUser' | translate"
              class="w-full"
              [showClear]="true"
              [filter]="true"
              appendTo="body"
            />
          </div>

          <!-- Priority -->
          <div class="flex flex-col gap-2">
            <label for="priority">{{ 'defect.priority' | translate }}</label>
            <p-select
              inputId="priority"
              [options]="priorityOptions"
              optionLabel="value"
              optionValue="key"
              formControlName="priority"
              [placeholder]="'defect.priority' | translate"
              class="w-full"
              [showClear]="true"
            />
          </div>

          <!-- Due Date -->
          <div class="flex flex-col gap-2">
            <label for="dueDate">{{ 'defect.dueDate' | translate }}</label>
            <p-datepicker
              inputId="dueDate"
              formControlName="dueDate"
              dateFormat="dd.mm.yy"
              [showClear]="true"
              class="w-full"
            />
          </div>

          <!-- Estimated Repair Time -->
          <div class="flex flex-col gap-2">
            <label for="estimatedRepairTime">{{
              'defect.estimatedRepairTime' | translate
            }}</label>
            <p-inputNumber
              id="estimatedRepairTime"
              formControlName="estimatedRepairTime"
              [placeholder]="'defect.estimatedRepairTime' | translate"
              [useGrouping]="false"
              mode="decimal"
              [maxFractionDigits]="2"
              suffix=" h"
              styleClass="w-full"
            ></p-inputNumber>
          </div>
          <!-- Repair Timing -->
          <div class="flex flex-col gap-2">
            <label>{{ 'defect.repairTiming' | translate }}</label>
            <p-selectbutton
              [options]="repairTimingOptions"
              optionLabel="value"
              optionValue="key"
              formControlName="repairTiming"
              [allowEmpty]="true"
            />
          </div>
        </div>
      </div>
      <div></div>

      <!-- Existing Images -->
      @if (defect()?.media?.length) {
        <div class="flex flex-col gap-2">
          <label>{{ 'defect.images' | translate }}</label>
          <div class="image-gallery flex flex-wrap gap-3">
            @for (media of defect()!.media; track media.id) {
              <div class="image-container">
                <i
                  class="delete-icon fa fa-times-circle text-red-500"
                  (click)="$event.stopPropagation(); deleteMedia(media)"
                ></i>
                <p-image
                  [src]="getMediaUrl(media.url)"
                  [alt]="media.name"
                  [preview]="true"
                  width="128"
                  height="128"
                  [imageStyle]="{
                    width: '128px',
                    height: '128px',
                    'object-fit': 'cover',
                    'border-radius': '0.5rem',
                  }"
                >
                  <ng-template #indicator>
                    <i class="fa fa-search"></i>
                  </ng-template>
                </p-image>
              </div>
            }
          </div>
        </div>
      }

      <!-- Upload Images -->
      <div class="flex flex-col gap-2">
        <label>{{ 'defect.uploadImages' | translate }}</label>
        <p-fileupload
          #fileUpload
          name="files[]"
          [multiple]="true"
          accept="image/*"
          [maxFileSize]="10485760"
          [auto]="false"
          [showUploadButton]="false"
          [chooseLabel]="'label.fileChoose' | translate"
          [cancelLabel]="'label.cancel' | translate"
        >
          <ng-template #empty>
            <div>{{ 'label.fileDragDrop' | translate }}</div>
          </ng-template>
        </p-fileupload>
      </div>

      <!-- Comment field in create mode (inside form) -->
      @if (!isEditMode()) {
        <div class="flex flex-col gap-2">
          <label for="newComment">{{ 'defect.addComment' | translate }}</label>
          <textarea
            pTextarea
            id="newComment"
            [ngModel]="newCommentText()"
            (ngModelChange)="newCommentText.set($event)"
            [ngModelOptions]="{ standalone: true }"
            [placeholder]="'defect.commentPlaceholder' | translate"
            rows="3"
            class="w-full"
          ></textarea>
        </div>
      }

      <!-- Action Buttons -->
      <div class="flex justify-end gap-2">
        <p-button
          [label]="'label.cancel' | translate"
          severity="secondary"
          (click)="navigateBack()"
        />
        <p-button [label]="'label.save' | translate" type="submit" />
      </div>
    </form>

    <!-- Comments Section (only in edit mode, inside main card as panel) -->
    @if (isEditMode()) {
      <p-panel
        [header]="'defect.comments' | translate"
        styleClass="bg-surface-100 mt-8"
      >
        <!-- New Comment (above existing comments) -->
        <div class="flex flex-col gap-2 mb-6">
          <label for="newComment">{{ 'defect.addComment' | translate }}</label>
          <textarea
            pTextarea
            id="newComment"
            [ngModel]="newCommentText()"
            (ngModelChange)="newCommentText.set($event)"
            [ngModelOptions]="{ standalone: true }"
            [placeholder]="'defect.commentPlaceholder' | translate"
            rows="3"
            class="w-full"
          ></textarea>
          <div class="flex justify-end">
            <p-button
              [label]="'defect.commentSend' | translate"
              (click)="submitComment()"
              [disabled]="!newCommentText().trim()"
            />
          </div>
        </div>

        <!-- Existing Comments (sorted newest first, paginated) -->
        @if (sortedComments().length) {
          <div class="flex flex-col gap-3">
            @for (comment of paginatedComments(); track comment.id) {
              <div class="bg-white border border-surface-200 rounded-lg p-4">
                <div class="flex justify-between items-center mb-2">
                  <span class="font-semibold">
                    {{ comment.user?.name ?? 'Unknown' }}
                  </span>
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-surface-500">
                      {{ comment.datetime | date: 'dd.MM.yyyy HH:mm' : 'Europe/Berlin' }}
                    </span>
                    @if (canDeleteComment(comment)) {
                      <p-button
                        icon="fa fa-trash"
                        [text]="true"
                        [rounded]="true"
                        severity="danger"
                        size="small"
                        (click)="removeComment(comment)"
                      />
                    }
                  </div>
                </div>
                <p class="m-0 whitespace-pre-line">{{ comment.comment }}</p>
              </div>
            }
          </div>

          @if (sortedComments().length > commentsPerPage) {
            <p-paginator
              styleClass="mt-4"
              [rows]="commentsPerPage"
              [totalRecords]="sortedComments().length"
              [first]="commentFirst()"
              (onPageChange)="onCommentPageChange($event)"
            />
          }
        }
      </p-panel>
    }

    <!-- Check-Ins Table (only in edit mode, when checkins exist) -->
    @if (isEditMode() && sortedCheckins().length) {
      <p-panel
        [header]="'defect.checkins' | translate"
        styleClass="bg-surface-100 mt-6"
      >
        <p-table
          [value]="sortedCheckins()"
          [rows]="10"
          [paginator]="sortedCheckins().length > 10"
        >
          <ng-template #header>
            <tr>
              <th>{{ 'defect.checkinUser' | translate }}</th>
              <th>{{ 'defect.checkinTime' | translate }}</th>
              <th>{{ 'defect.checkoutTime' | translate }}</th>
              <th>{{ 'defect.checkinDuration' | translate }}</th>
            </tr>
          </ng-template>
          <ng-template #body let-checkin>
            <tr>
              <td>{{ checkin.user?.name ?? 'Unknown' }}</td>
              <td>{{ checkin.checked_in_at | date: 'dd.MM.yyyy HH:mm' : 'Europe/Berlin' }}</td>
              <td>
                @if (checkin.checked_out_at) {
                  {{ checkin.checked_out_at | date: 'dd.MM.yyyy HH:mm' : 'Europe/Berlin' }}
                } @else {
                  <p-tag value="Aktiv" severity="success" />
                }
              </td>
              <td>{{ formatDuration(checkinDurationMinutes(checkin)) }}</td>
            </tr>
          </ng-template>
          <ng-template #footer>
            <tr>
              <td colspan="3" class="text-right font-bold">
                {{ 'defect.checkinTotal' | translate }}
              </td>
              <td class="font-bold">
                {{ formatDuration(totalCheckinMinutes()) }}
              </td>
            </tr>
          </ng-template>
        </p-table>
      </p-panel>
    }
  </p-card>
</div>
